import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Spinner,
} from "@heroui/react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import useEntities from "@/hooks/useEntities";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import addUserImgModal from "../../../adduser-modal.png";

type FormValues = {
  entity: string;
  assignment_id: string;
};

interface User {
  user_id: string;
  user_name: string;
  user_given_name?: string;
  user_email?: string;
  entities?: string;
  [key: string]: any;
}

export default function UpdateEntityModal({ isOpen, onClose, classNames, orderId }) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const { entities } = useEntities();

  const { handleSubmit, control, setValue, watch } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      entity: "",
      assignment_id: "",
    },
  });

  const entityValue = watch("entity");
  const assignmentValue = watch("assignment_id");

  const isFormValid = entityValue && assignmentValue;

  const entitiesList = entities;

  const { data: usersResponse } = useQuery<{
    data: User[];
    pagination: any;
  }>({
    queryKey: ["AllUsers"],
    enabled: false,
  });
  const allUsers = usersResponse?.data ?? [];

  const filteredUsers = useMemo(() => {
    if (!entityValue || !allUsers.length) return [];

    return allUsers.filter((user) => {
      if (!user.entities) return false;

      const userEntities = user.entities.split(",").map((e) => e.trim());
      return userEntities.includes(entityValue);
    });
  }, [entityValue, allUsers]);

  useEffect(() => {
    if (entityValue && assignmentValue) {
      const isUserInEntity = filteredUsers.some((user) => user.user_id === assignmentValue);
      if (!isUserInEntity) {
        setValue("assignment_id", "");
      }
    }
  }, [entityValue, assignmentValue, filteredUsers, setValue]);

  const fetchOrderData = async () => {
    if (!orderId) return;

    setIsLoading(true);
    try {
      const response = await axiosPrivate.get(`/orders/${orderId}`);
      console.log(response.data);

      if (response.data?.order?.metadata?.entity_name?.value) {
        setValue("entity", response.data.order.metadata.entity_name.value);
      }

      if (response.data?.process?.assignment?.assignment_id) {
        setValue("assignment_id", response.data.process.assignment.assignment_id);
      }

      setDataFetched(true);
    } catch (error) {
      console.error("Erreur lors du chargement des données de commande:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !dataFetched) {
      fetchOrderData();
    }
  }, [isOpen, dataFetched, orderId]);

  useEffect(() => {
    if (!isOpen) {
      if (dataFetched) {
        setDataFetched(false);
      }
    }
  }, [isOpen]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsLoading(true);
    try {
      await axiosPrivate.get(
        `/orders/${orderId}/reprocess?reprocessing_step=update_entity&entity_id=${data.entity}&assignment_id=${data.assignment_id}`
      );

      queryClient.invalidateQueries({ queryKey: ["orders"] });

      onClose();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la commande:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserDisplayName = (user) => {
    const emailDisplay = user.user_email ? ` (${user.user_email})` : "";

    if (user.user_given_name && user.user_name) {
      return `${user.user_given_name} ${user.user_name}${emailDisplay}`;
    } else {
      return user.user_id;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      placement="top-center"
      size="2xl"
      classNames={{
        backdrop: "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
      }}
    >
      <ModalContent
        as="form"
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
      >
        <ModalBody>
          <div className="flex pt-5">
            <div>
              <div className="text-2xl font-bold pt-3 pb-6">{t("update_entity")}</div>
              <div className="text-vistagreen">{t("fill_required_info_for_update_entity")}</div>
            </div>
            <img
              src={addUserImgModal}
              alt={t("alt_vistaflow_customer_settings")}
              className="w-48 origin-left self-center"
            />
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Spinner size="lg" color="primary" />
              <p className="mt-4 text-gray-600">{t("loading")}</p>
            </div>
          ) : (
            <div className="space-y-6">
              <Controller
                name="entity"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    placeholder={t("select")}
                    labelPlacement="outside"
                    aria-label={t("select_entity")}
                    className="max-w-lg"
                    disableSelectorIconRotation
                    disallowEmptySelection
                    selectedKeys={entityValue ? [entityValue] : []}
                    onChange={onChange}
                    data-cy={`modal__updateEntity-SelectEntity`}
                  >
                    {entitiesList.map((entity) => (
                      <SelectItem key={entity.entity_id} data-cy={`modal__updateEntity-SelectEntity-${entity.entity_id}`}>{entity.entity_name}</SelectItem>
                    ))}
                  </Select>
                )}
              />

              <Controller
                name="assignment_id"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    placeholder={t("select")}
                    labelPlacement="outside"
                    aria-label={t("select_user")}
                    className="max-w-lg"
                    disableSelectorIconRotation
                    disallowEmptySelection
                    selectedKeys={assignmentValue ? [assignmentValue] : []}
                    onChange={onChange}
                    isDisabled={!entityValue || filteredUsers.length === 0}
                    data-cy={`modal__updateEntity-SelectAssignment`}
                  >
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <SelectItem key={user.user_id} data-cy={`modal__updateEntity-SelectAssignment-${user.user_id}`}>{getUserDisplayName(user)}</SelectItem>
                      ))
                    ) : (
                      <SelectItem key="no-users" isDisabled>
                        {entityValue ? t("no_users_for_entity") : t("select_entity_first")}
                      </SelectItem>
                    )}
                  </Select>
                )}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button type="submit" isDisabled={!isFormValid} isLoading={isLoading} data-cy={`modal__updateEntity-submit`}>
            {t("update_entity")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
